import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import DataTable from "@/components/DataTable";
import ViaggioForm from "@/components/ViaggioForm";
import ImportViaggiDialog from "@/components/ImportViaggiDialog";
import ProgrammaDialog from "@/components/ProgrammaDialog";
import CollegaSubvettoreDialog from "@/components/CollegaSubvettoreDialog";
import { BlocchiList } from "@/components/BlocchiScadenze";
import { Textarea } from "@/components/ui/textarea";
import Combobox from "@/components/Combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDate, formatNumber } from "@/lib/format";
import { toast } from "sonner";
import { Pencil, Trash2, Upload, FileText, Plus, X, Link2, ShieldAlert } from "lucide-react";

const PAGE_SIZE = 25;

const STATO_BADGE = {
  pianificato: "bg-slate-100 text-slate-600 border-slate-200",
  eseguito: "bg-sky-50 text-sky-700 border-sky-200",
  annullato: "bg-red-50 text-red-700 border-red-200",
  prefatturato: "bg-amber-50 text-amber-700 border-amber-200",
  fatturato: "bg-green-50 text-green-700 border-green-200",
};

const BULK_ACTIONS = [
  { v: "stato", l: "Cambia stato" },
  { v: "autista_id", l: "Assegna autista" },
  { v: "trattore_id", l: "Assegna trattore" },
  { v: "rimorchio_id", l: "Assegna rimorchio" },
  { v: "subvettore_id", l: "Assegna sub-vettore" },
  { v: "delete", l: "Elimina selezionati" },
];

function qty(v) {
  const parts = [];
  if (v.quantita_litri) parts.push(`${formatNumber(v.quantita_litri, 0)} L`);
  if (v.quantita_kg) parts.push(`${formatNumber(v.quantita_kg, 0)} kg`);
  if (v.quantita_ton) parts.push(`${formatNumber(v.quantita_ton, 2)} t`);
  if (v.quantita_pezzi) parts.push(`${formatNumber(v.quantita_pezzi, 0)} pz`);
  return parts.join(" / ") || "—";
}

const txt = (label, isText) => (label ? <span className={isText ? "text-amber-700" : ""} title={isText ? "Non collegato ad anagrafica" : undefined}>{label}</span> : "—");

export default function Viaggi() {
  const [config, setConfig] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortBy, setSortBy] = useState("data_viaggio");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [filters, setFilters] = useState({});
  const [dateDa, setDateDa] = useState("");
  const [dateA, setDateA] = useState("");
  const [opts, setOpts] = useState({});
  const [selected, setSelected] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [programmaOpen, setProgrammaOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [bulkAction, setBulkAction] = useState("stato");
  const [bulkValue, setBulkValue] = useState("");
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [collegaOpen, setCollegaOpen] = useState(false);
  const [targheCount, setTargheCount] = useState(0);
  const [bulkBlocco, setBulkBlocco] = useState(null);
  const [bulkMotivo, setBulkMotivo] = useState("");

  const loadTarghe = useCallback(() => {
    api.get("/viaggi/targhe-non-collegate").then((r) => setTargheCount(r.data.length)).catch(() => {});
  }, []);

  useEffect(() => {
    loadTarghe();
  }, [loadTarghe]);

  useEffect(() => {
    api.get("/viaggi/config").then((r) => setConfig(r.data));
    Promise.all([
      api.get("/clienti/options"),
      api.get("/autisti/options"),
      api.get("/veicoli/options"),
      api.get("/subvettori/options"),
    ]).then(([cl, au, ve, sv]) =>
      setOpts({ clienti: cl.data, autisti: au.data, veicoli: ve.data, subvettori: sv.data })
    );
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const buildParams = useCallback(() => {
    const p = { page, page_size: PAGE_SIZE, sort_by: sortBy, sort_dir: sortDir };
    if (debounced) p.search = debounced;
    if (dateDa) p.data_da = dateDa;
    if (dateA) p.data_a = dateA;
    Object.entries(filters).forEach(([k, v]) => v && (p[k] = v));
    return p;
  }, [page, sortBy, sortDir, debounced, filters, dateDa, dateA]);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/viaggi", { params: buildParams() })
      .then((r) => {
        setRows(r.data.items);
        setTotal(r.data.total);
      })
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => {
    load();
  }, [load]);

  const onSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortBy(key);
      setSortDir("asc");
    }
  };

  const toggleRow = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAllPage = (pageRows, checked) =>
    setSelected((s) => {
      const ids = pageRows.map((r) => r.id);
      return checked ? [...new Set([...s, ...ids])] : s.filter((x) => !ids.includes(x));
    });

  const doExport = async () => {
    try {
      const params = buildParams();
      delete params.page;
      delete params.page_size;
      const res = await api.get("/viaggi/export", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "viaggi.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/viaggi/${toDelete.id}`);
      toast.success("Viaggio eliminato");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const applyBulk = async (motivo) => {
    setBulkConfirm(false);
    try {
      const payload = { ids: selected, action: bulkAction };
      if (bulkAction !== "delete") payload.value = bulkValue;
      if (motivo) payload.override_motivo = motivo;
      const { data } = await api.post("/viaggi/bulk", payload);
      toast.success(`Operazione applicata a ${data.count} viaggi`);
      setSelected([]);
      setBulkValue("");
      setBulkBlocco(null);
      setBulkMotivo("");
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.blocchi) {
        setBulkBlocco(d);
        return;
      }
      toast.error(apiError(e));
    }
  };

  const columns = useMemo(
    () => [
      { key: "data_viaggio", label: "Data", nowrap: true, render: (r) => formatDate(r.data_viaggio) },
      { key: "codice_viaggio", label: "Codice", render: (r) => <span className="font-medium text-slate-800">{r.codice_viaggio}</span> },
      { key: "cliente_nome", label: "Cliente", sortable: false, render: (r) => txt(r.cliente_nome, !r.cliente_id) },
      { key: "autista_nome", label: "Autista", sortable: false, render: (r) => txt(r.autista_nome, !r.autista_id) },
      { key: "trattore_targa", label: "Trattore", sortable: false, render: (r) => txt(r.trattore_targa, !r.trattore_id) },
      { key: "rimorchio_targa", label: "Rimorchio", sortable: false, render: (r) => txt(r.rimorchio_targa, !r.rimorchio_id) },
      { key: "tratta", label: "Tratta", sortable: false, render: (r) => (r.partenza_nome || r.arrivo_nome ? <span className="text-xs">{r.partenza_nome || "?"} <span className="text-slate-400">→</span> {r.arrivo_nome || "?"}</span> : "—") },
      { key: "prodotto_nome", label: "Prodotto", sortable: false, render: (r) => txt(r.prodotto_nome, !r.prodotto_id) },
      { key: "quantita", label: "Quantità", sortable: false, align: "right", render: (r) => qty(r) },
      { key: "numero_scarichi", label: "Scar.", align: "right", render: (r) => (r.numero_scarichi != null ? formatNumber(r.numero_scarichi, 0) : "—") },
      { key: "numero_ddt", label: "DDT", sortable: false, render: (r) => <span className="text-xs font-mono">{r.numero_ddt || "—"}</span> },
      { key: "km", label: "Km", align: "right", render: (r) => (r.km != null ? formatNumber(r.km, 0) : "—") },
      { key: "tipologia", label: "Tipologia", sortable: false, render: (r) => <span className="text-xs capitalize text-slate-600">{r.tipologia}</span> },
      { key: "stato", label: "Stato", render: (r) => (
        <span className="flex items-center gap-1">
          <Badge variant="outline" className={`capitalize ${STATO_BADGE[r.stato] || ""}`}>{r.stato}</Badge>
          {r.override_scadenze && (
            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 gap-1" title={`Forzato da ${r.override_scadenze.utente}: ${r.override_scadenze.motivo}`} data-testid={`override-badge-${r.id}`}>
              <ShieldAlert className="h-3 w-3" />Override
            </Badge>
          )}
        </span>
      ) },
    ],
    []
  );

  const filterFields = useMemo(() => {
    if (!config) return [];
    return [
      { name: "stato", label: "Stato", options: config.stati.map((s) => ({ value: s, label: s })) },
      { name: "tipologia", label: "Tipologia", options: config.tipologie.map((t) => ({ value: t, label: t })) },
      { name: "cliente_id", label: "Cliente", options: (opts.clienti || []).map((o) => ({ value: o.value, label: o.label })) },
      { name: "autista_id", label: "Autista", options: (opts.autisti || []).map((o) => ({ value: o.value, label: o.label })) },
    ];
  }, [config, opts]);

  const canWrite = config?.can_write;

  const leftToolbar = (
    <div className="flex items-center gap-2">
      <Input type="date" className="h-9 w-[140px]" value={dateDa} onChange={(e) => { setDateDa(e.target.value); setPage(1); }} data-testid="viaggi-data-da" title="Data da" />
      <Input type="date" className="h-9 w-[140px]" value={dateA} onChange={(e) => { setDateA(e.target.value); setPage(1); }} data-testid="viaggi-data-a" title="Data a" />
      {canWrite && (
        <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="viaggi-import">
          <Upload className="h-4 w-4 mr-1.5" />Importa
        </Button>
      )}
      {canWrite && (
        <Button variant="outline" size="sm" className={`h-9 ${targheCount ? "border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100" : ""}`} onClick={() => setCollegaOpen(true)} data-testid="viaggi-collega-subvettori">
          <Link2 className="h-4 w-4 mr-1.5" />Collega sub-vettori{targheCount ? ` (${targheCount})` : ""}
        </Button>
      )}
      <Button variant="outline" size="sm" className="h-9" onClick={() => setProgrammaOpen(true)} data-testid="viaggi-programma">
        <FileText className="h-4 w-4 mr-1.5" />Programma
      </Button>
    </div>
  );

  const bulkValueControl = () => {
    if (bulkAction === "delete") return null;
    if (bulkAction === "stato")
      return (
        <Select value={bulkValue} onValueChange={setBulkValue}>
          <SelectTrigger className="h-8 w-44" data-testid="bulk-value"><SelectValue placeholder="Nuovo stato..." /></SelectTrigger>
          <SelectContent>{config?.stati.map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}</SelectContent>
        </Select>
      );
    const source = bulkAction === "autista_id" ? opts.autisti : bulkAction === "subvettore_id" ? opts.subvettori : opts.veicoli;
    return <div className="w-56"><Combobox options={source || []} value={bulkValue} onChange={setBulkValue} placeholder="Seleziona..." testid="bulk-value" /></div>;
  };

  return (
    <div className="space-y-3">
      {selected.length > 0 && (
        <div className="bg-sky-50 border border-sky-200 rounded-md p-2.5 flex flex-wrap items-center gap-2" data-testid="bulk-bar">
          <span className="text-sm font-medium text-sky-800 px-1">{selected.length} selezionati</span>
          <Select value={bulkAction} onValueChange={(v) => { setBulkAction(v); setBulkValue(""); }}>
            <SelectTrigger className="h-8 w-52" data-testid="bulk-action"><SelectValue /></SelectTrigger>
            <SelectContent>{BULK_ACTIONS.map((a) => (<SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>))}</SelectContent>
          </Select>
          {bulkValueControl()}
          <Button size="sm" className="h-8" data-testid="bulk-apply" onClick={() => setBulkConfirm(true)} disabled={bulkAction !== "delete" && !bulkValue}>
            Applica
          </Button>
          <Button variant="ghost" size="sm" className="h-8 text-slate-500" onClick={() => setSelected([])}>
            <X className="h-4 w-4 mr-1" />Deseleziona
          </Button>
        </div>
      )}

      <DataTable
        testid="table-viaggi"
        columns={columns}
        rows={rows}
        loading={loading || !config}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        onPage={setPage}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
        search={search}
        onSearch={setSearch}
        filterFields={filterFields}
        filterValues={filters}
        onFilter={(name, val) => { setFilters((p) => ({ ...p, [name]: val })); setPage(1); }}
        onExport={doExport}
        onNew={() => { setEditing(null); setFormOpen(true); }}
        newLabel="Nuovo viaggio"
        canWrite={canWrite}
        leftToolbar={leftToolbar}
        selectable
        selectedIds={selected}
        onToggleRow={toggleRow}
        onToggleAllPage={toggleAllPage}
        emptyLabel="Nessun viaggio. Inserisci manualmente o importa da CSV/Excel."
        renderActions={
          canWrite
            ? (row) => (
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-sky-700" onClick={() => { setEditing(row); setFormOpen(true); }} data-testid={`viaggio-edit-${row.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-slate-500 hover:text-red-600" onClick={() => setToDelete(row)} data-testid={`viaggio-delete-${row.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              )
            : undefined
        }
      />

      {config && (
        <ViaggioForm open={formOpen} onOpenChange={setFormOpen} config={config} initial={editing} onSaved={load} />
      )}
      <ImportViaggiDialog open={importOpen} onOpenChange={setImportOpen} config={config} onImported={load} />
      <ProgrammaDialog open={programmaOpen} onOpenChange={setProgrammaOpen} />
      <CollegaSubvettoreDialog open={collegaOpen} onOpenChange={setCollegaOpen} onLinked={() => { load(); loadTarghe(); }} />

      <AlertDialog open={!!bulkBlocco} onOpenChange={(v) => !v && setBulkBlocco(null)}>
        <AlertDialogContent data-testid="bulk-blocco-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700"><ShieldAlert className="h-5 w-5" />Assegnazione bloccata: documenti scaduti</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-slate-700">
                <BlocchiList blocchi={bulkBlocco?.blocchi || []} />
                {bulkBlocco?.can_override ? (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-slate-800">Forzatura amministrativa — motivazione obbligatoria</p>
                    <Textarea rows={2} className="mt-1" value={bulkMotivo} onChange={(e) => setBulkMotivo(e.target.value)} data-testid="bulk-override-motivo" />
                  </div>
                ) : (
                  <p className="mt-3 text-xs">Solo un amministratore può forzare l'assegnazione.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setBulkMotivo("")}>Annulla</AlertDialogCancel>
            {bulkBlocco?.can_override && (
              <AlertDialogAction onClick={() => applyBulk(bulkMotivo.trim())} disabled={!bulkMotivo.trim()} className="bg-red-600 hover:bg-red-700" data-testid="bulk-override-confirm">Forza assegnazione</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il viaggio {toDelete?.codice_viaggio}?</AlertDialogTitle>
            <AlertDialogDescription>Eliminazione logica, registrata nel log attività.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="viaggio-delete-confirm">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'operazione su {selected.length} viaggi?</AlertDialogTitle>
            <AlertDialogDescription>
              {bulkAction === "delete" ? "I viaggi selezionati verranno eliminati (logicamente)." : "Verrà applicata la modifica selezionata a tutti i viaggi selezionati."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={() => applyBulk()} className={bulkAction === "delete" ? "bg-red-600 hover:bg-red-700" : ""} data-testid="bulk-confirm">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
